'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  HelpCircle,
  Plus,
  MessageCircle,
  CheckCircle,
  Clock,
  User,
  Send,
  Image as ImageIcon,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import type { Question, Answer } from '../../types';
import { MOCK_QUESTIONS, MOCK_USERS } from '../../data/mock-data';

export function QnAView() {
  const [questions, setQuestions] = useState<Question[]>(MOCK_QUESTIONS);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedQuestionId, setExpandedQuestionId] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [newQuestion, setNewQuestion] = useState({
    nickname: '',
    question: '',
    imagePreview: ''
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;

    return `${date.getMonth() + 1}월 ${date.getDate()}일`;
  };

  const handleAddQuestion = () => {
    if (newQuestion.nickname && newQuestion.question) {
      const newQ: Question = {
        id: `q-${Date.now()}`,
        user_nickname: newQuestion.nickname,
        image_url: newQuestion.imagePreview || '/placeholder-question.jpg',
        question: newQuestion.question,
        created_at: new Date().toISOString(),
        is_solved: false,
        answers: []
      };
      setQuestions(prev => [newQ, ...prev]);
      setNewQuestion({ nickname: '', question: '', imagePreview: '' });
      setShowAddForm(false);
    }
  };

  const handleAddAnswer = (questionId: string) => {
    if (!answerText.trim()) return;

    const currentUser = MOCK_USERS[0]; // 데모용: 첫 번째 사용자로 답변

    const newAnswer: Answer = {
      id: `a-${Date.now()}`,
      user_nickname: currentUser.nickname,
      content: answerText,
      created_at: new Date().toISOString()
    };

    setQuestions(prev =>
      prev.map(q =>
        q.id === questionId
          ? { ...q, answers: [...q.answers, newAnswer] }
          : q
      )
    );
    setAnswerText('');
  };

  const toggleSolved = (questionId: string) => {
    setQuestions(prev =>
      prev.map(q =>
        q.id === questionId
          ? { ...q, is_solved: !q.is_solved }
          : q
      )
    );
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 데모용: 실제로는 업로드 후 URL 반환
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewQuestion(prev => ({
          ...prev,
          imagePreview: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="p-4 pb-8 space-y-6">
        {/* 새 질문 버튼 */}
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="w-full h-12 bg-blue-500 hover:bg-blue-600 flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          질문하기
        </Button>

        {/* 새 질문 폼 */}
        {showAddForm && (
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">닉네임</label>
              <select
                value={newQuestion.nickname}
                onChange={(e) => setNewQuestion(prev => ({ ...prev, nickname: e.target.value }))}
                className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">닉네임 선택</option>
                {MOCK_USERS.map(user => (
                  <option key={user.id} value={user.nickname}>
                    {user.nickname}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">문제 이미지</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                {newQuestion.imagePreview ? (
                  <div className="relative">
                    <img
                      src={newQuestion.imagePreview}
                      alt="Preview"
                      className="max-h-48 mx-auto rounded"
                    />
                    <button
                      onClick={() => setNewQuestion(prev => ({ ...prev, imagePreview: '' }))}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">클릭하여 이미지 업로드</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">질문 내용</label>
              <Textarea
                value={newQuestion.question}
                onChange={(e) => setNewQuestion(prev => ({ ...prev, question: e.target.value }))}
                placeholder="모르는 문제에 대해 질문해주세요"
                rows={3}
                className="resize-none"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleAddQuestion}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
                disabled={!newQuestion.nickname || !newQuestion.question}
              >
                질문 등록
              </Button>
              <Button
                onClick={() => {
                  setShowAddForm(false);
                  setNewQuestion({ nickname: '', question: '', imagePreview: '' });
                }}
                variant="outline"
                className="flex-1"
              >
                취소
              </Button>
            </div>
          </div>
        )}

        {/* 질문 목록 */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-500" />
            질문 게시판
            <Badge variant="secondary">{questions.length}개</Badge>
          </h2>

          {questions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <HelpCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">질문이 없습니다</p>
              <p className="text-sm">모르는 문제가 있으면 질문해보세요!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {questions.map((question) => (
                <div
                  key={question.id}
                  className={`border-2 rounded-lg overflow-hidden ${
                    question.is_solved
                      ? 'border-green-200 bg-green-50'
                      : 'border-blue-200 bg-blue-50'
                  }`}
                >
                  {/* 질문 헤더 */}
                  <div className="p-4">
                    {/* 이미지 */}
                    <div className="bg-gray-200 rounded-lg mb-3 overflow-hidden">
                      {question.image_url ? (
                        <img
                          src={question.image_url}
                          alt="문제 이미지"
                          className="w-full aspect-video object-cover"
                        />
                      ) : (
                        <div className="aspect-video flex items-center justify-center bg-gray-100">
                          <ImageIcon className="w-12 h-12 text-gray-400" />
                          <span className="ml-2 text-gray-500 text-sm">문제 이미지</span>
                        </div>
                      )}
                    </div>

                    {/* 작성자 정보 */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">{question.user_nickname}</span>
                        <span className="text-sm text-gray-500">·</span>
                        <span className="text-sm text-gray-500">{formatDate(question.created_at)}</span>
                      </div>
                      <button
                        onClick={() => toggleSolved(question.id)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
                          question.is_solved
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        {question.is_solved ? '해결됨' : '미해결'}
                      </button>
                    </div>

                    {/* 질문 내용 */}
                    <p className="text-gray-800">{question.question}</p>

                    {/* 답변 토글 */}
                    <button
                      onClick={() => setExpandedQuestionId(
                        expandedQuestionId === question.id ? null : question.id
                      )}
                      className="flex items-center gap-2 mt-3 text-blue-600 hover:text-blue-700"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm">답변 {question.answers.length}개</span>
                      {expandedQuestionId === question.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {/* 답변 섹션 */}
                  {expandedQuestionId === question.id && (
                    <div className="border-t border-gray-200 bg-white p-4 space-y-4">
                      {/* 기존 답변들 */}
                      {question.answers.length > 0 && (
                        <div className="space-y-3">
                          {question.answers.map((answer) => (
                            <div
                              key={answer.id}
                              className="bg-gray-50 p-3 rounded-lg"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <User className="w-3 h-3 text-gray-500" />
                                <span className="font-medium text-sm">{answer.user_nickname}</span>
                                <span className="text-xs text-gray-500">
                                  {formatDate(answer.created_at)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">{answer.content}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* 답변 입력 */}
                      <div className="flex gap-2">
                        <Textarea
                          value={answerText}
                          onChange={(e) => setAnswerText(e.target.value)}
                          placeholder="답변을 입력하세요"
                          rows={2}
                          className="flex-1 resize-none text-sm"
                        />
                        <Button
                          onClick={() => handleAddAnswer(question.id)}
                          size="icon"
                          className="h-auto bg-blue-500 hover:bg-blue-600"
                          disabled={!answerText.trim()}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
